import { useMemo } from "react"
import Box from "@mui/material/Box"
import dayjs from "dayjs"
import { EChartsOption } from "echarts"
import { Icon, IconColor } from "../shared/Icon"
import { VitalsGraphChartProps } from "@/api/FacilityLevelsService"
import Chart from "@/components/shared/Chart"
import "./index.scss"

const ROW_LABEL = "Room"

export function buildOccupancyHeatmapOption(
   data: VitalsGraphChartProps["data"],
   timezone: VitalsGraphChartProps["timezone"]
): EChartsOption {
   if (!data) return {}

   const presenceIndex = data.header.indexOf("human_presence")

   const timestamps = Object.keys(data.data).sort(
      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
   )
   const categories = timestamps.map((ts) =>
      dayjs(ts).tz(timezone).format("MMM D HH:mm")
   )

   const cells: [number, number, number][] = timestamps
      .map((timestamp, index): [number, number, number] | null => {
         const presenceRatio = data.data[timestamp][presenceIndex]
         if (presenceRatio === null || presenceRatio === undefined) return null
         return [index, 0, presenceRatio]
      })
      .filter((cell): cell is [number, number, number] => cell !== null)

   return {
      tooltip: {
         trigger: "item",
         formatter: (params) => {
            const point = Array.isArray(params) ? params[0] : params
            const [xIndex, , presenceRatio] = point.value as [
               number,
               number,
               number,
            ]
            return `${categories[xIndex]} — ${Math.round(presenceRatio * 100)}% present`
         },
      },
      grid: { top: 16, bottom: 36, left: 64, right: 16 },
      xAxis: {
         type: "category",
         data: categories,
         axisTick: { show: false },
      },
      yAxis: {
         type: "category",
         data: [ROW_LABEL],
         axisTick: { show: false },
         axisLine: { show: false },
      },
      visualMap: {
         min: 0,
         max: 1,
         show: false,
         inRange: { color: ["#e8f5e9", "#43a047", "#1b5e20"] },
      },
      series: [
         {
            type: "heatmap",
            data: cells,
         },
      ],
   }
}

const OccupancyHeatMap = ({
   data,
   loading,
   timezone,
}: VitalsGraphChartProps) => {
   const option = useMemo(
      () => buildOccupancyHeatmapOption(data, timezone),
      [data, timezone]
   )
   const isEmpty = !data || Object.keys(data.data).length === 0

   return (
      <Box sx={{ width: "100%" }}>
         <div className="occupancy-heat-map__title">
            <Icon
               name="graph"
               color={IconColor.HONOLULU_BLUE}
            />
            Occupancy Trend Heat Map
         </div>
         {isEmpty && !loading ? (
            <div className="occupancy-heat-map--empty">
               No vitals recorded for this range.
            </div>
         ) : (
            <Chart
               option={option}
               loading={loading}
               height={120}
            />
         )}
      </Box>
   )
}

export default OccupancyHeatMap
